import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, CurrencyInr, QrCode, CheckCircle, Spinner, Copy, ArrowClockwise, PencilSimple, Receipt, Bell, Export, CreditCard } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

const STATUS_COLOR = { paid: '#10A882', partial: '#E5A100', pending: '#2B6EF0', overdue: '#EF4444' };
const fmt = (n) => `Rs.${(n || 0).toLocaleString('en-IN')}`;

export default function FeesPage() {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInv, setShowInv] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showUPI, setShowUPI] = useState(false);
  const [upiData, setUpiData] = useState(null);
  const [upiStatus, setUpiStatus] = useState('idle');
  const pollRef = useRef(null);
  const [invF, setInvF] = useState({ student_name: '', type: 'tuition', amount: 0, due_date: '' });
  const [payF, setPayF] = useState({ invoice_id: '', amount: 0, method: 'upi' });
  const [showEdit, setShowEdit] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [editF, setEditF] = useState({ student_name: '', type: 'tuition', amount: 0, discount_amount: 0, discount_reason: '', due_date: '', notes: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [i, p, f] = await Promise.all([
        api.get('/api/invoices'),
        api.get('/api/payments'),
        api.get('/api/analytics/finance'),
      ]);
      setInvoices(i.data.invoices || []);
      setPayments(p.data.payments || []);
      setFin(f.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleInv = async (e) => {
    e.preventDefault();
    try { await api.post('/api/invoices', invF); toast.success('Invoice created'); setShowInv(false); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    try { await api.post('/api/payments', payF); toast.success('Payment recorded'); setShowPay(false); setPayF({ invoice_id: '', amount: 0, method: 'upi' }); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const openUPIDialog = async (invoice) => {
    const due = (invoice.net_amount || 0) - (invoice.paid_amount || 0);
    if (due <= 0) { toast.error('Invoice fully paid'); return; }
    setUpiStatus('generating');
    setUpiData(null);
    setShowUPI(true);
    try {
      const res = await api.post('/api/payments/upi-qr', { invoice_id: invoice.id });
      setUpiData(res.data);
      setUpiStatus('pending');
      startPolling(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate QR');
      setUpiStatus('error');
    }
  };

  const startPolling = (paymentId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/api/payments/${paymentId}/status`);
        if (res.data.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setUpiStatus('completed');
          fetchAll();
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const confirmManually = async () => {
    if (!upiData?.id) return;
    setUpiStatus('confirming');
    try {
      await api.put(`/api/payments/${upiData.id}/upi-confirm`);
      setUpiStatus('completed');
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      fetchAll();
      toast.success('Payment confirmed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Confirmation failed');
      setUpiStatus('pending');
    }
  };

  const closeUPI = () => {
    setShowUPI(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setUpiData(null);
    setUpiStatus('idle');
  };

  const copyUPI = () => {
    if (upiData?.upi_id) {
      navigator.clipboard.writeText(upiData.upi_id);
      toast.success('UPI ID copied');
    }
  };

  const openEditDialog = (inv) => {
    setEditInv(inv);
    setEditF({
      student_name: inv.student_name || '',
      type: inv.type || 'tuition',
      amount: inv.amount || inv.net_amount || 0,
      discount_amount: inv.discount_amount || 0,
      discount_reason: inv.discount_reason || '',
      due_date: inv.due_date || '',
      notes: inv.notes || '',
    });
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/invoices/${editInv.id}`, editF);
      toast.success('Invoice updated');
      setShowEdit(false);
      setEditInv(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const openReceipt = async (inv) => {
    try {
      const res = await api.get(`/api/invoices/${inv.id}/receipt`);
      const d = res.data;
      const paymentsHtml = (d.payments || []).map(p =>
        `<tr><td>${p.receipt_number || '-'}</td><td>${p.method || '-'}</td><td>Rs.${(p.amount || 0).toLocaleString('en-IN')}</td><td>${p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '-'}</td><td>${p.transaction_id || '-'}</td></tr>`
      ).join('');
      const html = `<!DOCTYPE html><html><head><title>Receipt - ${d.invoice?.invoice_number || ''}</title>
        <style>body{font-family:'IBM Plex Sans',Arial,sans-serif;max-width:700px;margin:40px auto;padding:20px;color:#09090B}
        h1{font-family:'Outfit',sans-serif;font-size:24px;margin-bottom:4px}
        .meta{color:#71717a;font-size:13px;margin-bottom:24px}
        table{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px}
        th,td{border:1px solid #e4e4e7;padding:8px 12px;text-align:left}
        th{background:#f4f4f5;font-weight:600;text-transform:uppercase;font-size:10px;letter-spacing:0.05em}
        .summary{margin-top:24px;font-size:14px}.summary span{font-weight:700}
        .status{display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:700;text-transform:uppercase}
        .footer{margin-top:40px;padding-top:16px;border-top:1px solid #e4e4e7;font-size:11px;color:#a1a1aa;text-align:center}
        @media print{body{margin:0;padding:20px}}</style></head><body>
        <h1>${d.branch?.name || 'Admitrix Academy'}</h1>
        <p class="meta">${d.branch?.address || ''}</p>
        <hr/>
        <h2 style="font-size:18px;margin-top:20px">Fee Receipt</h2>
        <p><strong>Invoice:</strong> ${d.invoice?.invoice_number || '-'} &nbsp; <span class="status" style="background:${d.invoice?.status === 'paid' ? '#dcfce7;color:#10A882' : '#fef3c7;color:#E5A100'}">${d.invoice?.status || '-'}</span></p>
        <p><strong>Student:</strong> ${d.invoice?.student_name || '-'}</p>
        ${d.lead ? `<p><strong>Parent Phone:</strong> ${d.lead.parent_phone || '-'}</p>` : ''}
        <p><strong>Type:</strong> ${d.invoice?.type || '-'} &nbsp; <strong>Due Date:</strong> ${d.invoice?.due_date || '-'}</p>
        <div class="summary">
          <p>Total Amount: <span>Rs.${(d.invoice?.amount || d.invoice?.net_amount || 0).toLocaleString('en-IN')}</span></p>
          ${d.invoice?.discount_amount ? `<p>Discount: <span>Rs.${(d.invoice.discount_amount).toLocaleString('en-IN')}</span> ${d.invoice.discount_reason ? '(' + d.invoice.discount_reason + ')' : ''}</p>` : ''}
          <p>Net Amount: <span>Rs.${(d.invoice?.net_amount || 0).toLocaleString('en-IN')}</span></p>
          <p>Paid: <span style="color:#10A882">Rs.${(d.invoice?.paid_amount || 0).toLocaleString('en-IN')}</span></p>
          <p>Outstanding: <span style="color:#EF4444">Rs.${((d.invoice?.net_amount || 0) - (d.invoice?.paid_amount || 0)).toLocaleString('en-IN')}</span></p>
        </div>
        ${(d.payments || []).length > 0 ? `<h3 style="font-size:15px;margin-top:28px">Payments</h3>
        <table><thead><tr><th>Receipt #</th><th>Method</th><th>Amount</th><th>Date</th><th>Transaction ID</th></tr></thead><tbody>${paymentsHtml}</tbody></table>` : ''}
        ${d.invoice?.notes ? `<p style="margin-top:20px;font-size:13px"><strong>Notes:</strong> ${d.invoice.notes}</p>` : ''}
        <div class="footer">Generated by Admitrix &bull; ${new Date().toLocaleString('en-IN')}</div>
        </body></html>`;
      const w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load receipt');
    }
  };

  const sendReminder = async (inv) => {
    try {
      const res = await api.post(`/api/invoices/${inv.id}/remind`);
      toast.success(`Reminder sent to ${res.data.to || 'parent'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const exportCSV = () => {
    const headers = ['Invoice #', 'Student', 'Type', 'Amount', 'Net Amount', 'Paid', 'Outstanding', 'Due Date', 'Status', 'Created'];
    const rows = invoices.map(inv => [
      inv.invoice_number || '',
      inv.student_name || '',
      inv.type || '',
      inv.amount || inv.net_amount || 0,
      inv.net_amount || 0,
      inv.paid_amount || 0,
      (inv.net_amount || 0) - (inv.paid_amount || 0),
      inv.due_date || '',
      inv.status || '',
      inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-IN') : '',
    ]);
    const csvContent = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayOnline = async (invoice) => {
    const loaded = await loadRazorpay();
    if (!loaded) { toast.error('Failed to load payment gateway'); return; }

    const outstanding = (invoice.net_amount || 0) - (invoice.paid_amount || 0);
    try {
      const { data } = await api.post('/api/payments/razorpay/create-order', {
        invoice_id: invoice.id,
        amount: outstanding,
      });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: 'Admitrix',
        description: `Invoice #${invoice.invoice_number || ''}`,
        theme: { color: '#E04B2A' },
        handler: async (response) => {
          try {
            await api.post('/api/payments/razorpay/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              invoice_id: data.invoice_id,
              amount: outstanding,
            });
            toast.success('Payment successful!');
            fetchAll();
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment order');
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  const unpaidInvoices = invoices.filter(i => i.status !== 'paid');

  return (
    <div data-testid="fees-page" className="stagger-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Fees & Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">Invoice & payment management</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-csv-btn" variant="outline" className="h-9 rounded-lg text-xs gap-1" onClick={exportCSV}><Export size={14} /> Export</Button>
          <Dialog open={showInv} onOpenChange={setShowInv}>
            <DialogTrigger asChild><Button data-testid="create-invoice-btn" variant="outline" className="h-9 rounded-lg text-xs gap-1"><Plus size={14} /> Invoice</Button></DialogTrigger>
            <DialogContent className="rounded-xl max-w-md"><DialogHeader><DialogTitle className="font-display text-foreground text-lg">Create Invoice</DialogTitle></DialogHeader>
              <form onSubmit={handleInv} className="space-y-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Student *</Label><Input data-testid="inv-student" value={invF.student_name} onChange={e => setInvF({...invF, student_name: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Type</Label>
                    <select value={invF.type} onChange={e => setInvF({...invF, type: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground"><option value="registration">Registration</option><option value="admission">Admission</option><option value="tuition">Tuition</option><option value="material">Material</option><option value="exam">Exam</option></select></div>
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Amount *</Label><Input data-testid="inv-amount" type="number" value={invF.amount} onChange={e => setInvF({...invF, amount: parseFloat(e.target.value) || 0})} className="h-9 rounded-lg text-sm" required /></div>
                </div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Due Date</Label><Input type="date" value={invF.due_date} onChange={e => setInvF({...invF, due_date: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
                <Button type="submit" data-testid="submit-invoice-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>Create</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={showPay} onOpenChange={setShowPay}>
            <DialogTrigger asChild><Button data-testid="record-payment-btn" className="h-9 rounded-lg text-xs gap-1 font-bold text-white" style={{ backgroundColor: '#E04B2A' }}><CurrencyInr size={14} /> Record Payment</Button></DialogTrigger>
            <DialogContent className="rounded-xl max-w-md"><DialogHeader><DialogTitle className="font-display text-foreground text-lg">Record Payment</DialogTitle></DialogHeader>
              <form onSubmit={handlePay} className="space-y-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Invoice *</Label>
                  <select data-testid="pay-invoice" value={payF.invoice_id} onChange={e => setPayF({...payF, invoice_id: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground" required><option value="">Select</option>{unpaidInvoices.map(i => (<option key={i.id} value={i.id}>{i.invoice_number} - {i.student_name} ({fmt(i.net_amount - i.paid_amount)} due)</option>))}</select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Amount *</Label><Input data-testid="pay-amount" type="number" value={payF.amount} onChange={e => setPayF({...payF, amount: parseFloat(e.target.value) || 0})} className="h-9 rounded-lg text-sm" required /></div>
                  <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Method</Label>
                    <select value={payF.method} onChange={e => setPayF({...payF, method: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground"><option value="upi">UPI</option><option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option><option value="cheque">Cheque</option><option value="card">Card</option></select></div>
                </div>
                <Button type="submit" data-testid="submit-payment-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>Record</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Finance KPIs */}
      {fin && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="finance-kpis">
          <KPI label="Total Billed" value={fmt(fin.total_amount)} accent="#2B6EF0" />
          <KPI label="Collected" value={fmt(fin.collected_amount)} accent="#10A882" />
          <KPI label="Outstanding" value={fmt(fin.outstanding)} accent="#EF4444" />
          <KPI label="Collection Rate" value={`${fin.collection_rate}%`} accent="#E5A100" />
        </div>
      )}

      <Tabs defaultValue="invoices">
        <TabsList className="bg-secondary rounded-lg h-9">
          <TabsTrigger value="invoices" data-testid="tab-invoices" className="text-xs rounded-md font-mono">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="text-xs rounded-md font-mono">Payments ({payments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <div className="border border-border rounded-xl bg-card overflow-hidden">
            <Table><TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Invoice #</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Student</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Type</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider text-right">Amount</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider text-right">Paid</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Due</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider text-center">Actions</TableHead>
            </TableRow></TableHeader><TableBody>{invoices.map(inv => {
              const due = (inv.net_amount || 0) - (inv.paid_amount || 0);
              return (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <TableCell className="text-[11px] py-2.5 font-mono">{inv.invoice_number}</TableCell>
                  <TableCell className="text-[12px] font-semibold py-2.5">{inv.student_name}</TableCell>
                  <TableCell className="text-[11px] text-muted-foreground py-2.5 capitalize">{inv.type}</TableCell>
                  <TableCell className="text-[11px] text-foreground py-2.5 text-right font-mono">{fmt(inv.net_amount)}</TableCell>
                  <TableCell className="text-[11px] text-foreground py-2.5 text-right font-mono">{fmt(inv.paid_amount)}</TableCell>
                  <TableCell className="text-[10px] text-muted-foreground py-2.5 font-mono">{inv.due_date || '-'}</TableCell>
                  <TableCell className="py-2.5"><span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: STATUS_COLOR[inv.status] || '#808080' }}>{inv.status}</span></TableCell>
                  <TableCell className="py-2.5 text-center">
                    <div className="inline-flex items-center gap-1">
                      {inv.status !== 'paid' && (
                        <button
                          onClick={() => openEditDialog(inv)}
                          className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary"
                          data-testid={`edit-invoice-${inv.id}`}
                          title="Edit invoice"
                        >
                          <PencilSimple size={12} weight="bold" />
                        </button>
                      )}
                      <button
                        onClick={() => openReceipt(inv)}
                        className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary"
                        data-testid={`receipt-${inv.id}`}
                        title="View receipt"
                      >
                        <Receipt size={12} weight="bold" />
                      </button>
                      {due > 0 && (
                        <>
                          <button
                            onClick={() => sendReminder(inv)}
                            className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-1 rounded-md transition-colors"
                            style={{ color: '#E5A100' }}
                            data-testid={`remind-${inv.id}`}
                            title="Send payment reminder"
                          >
                            <Bell size={12} weight="bold" />
                          </button>
                          <button
                            onClick={() => openUPIDialog(inv)}
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md transition-colors"
                            style={{ color: '#2B6EF0', backgroundColor: 'rgba(43,110,240,0.08)' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(43,110,240,0.15)'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(43,110,240,0.08)'}
                            data-testid={`upi-collect-${inv.id}`}
                          >
                            <QrCode size={12} weight="bold" /> UPI
                          </button>
                          <button
                            onClick={() => handlePayOnline(inv)}
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md transition-colors"
                            style={{ color: '#10A882', backgroundColor: 'rgba(16,168,130,0.08)' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(16,168,130,0.15)'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(16,168,130,0.08)'}
                            data-testid={`pay-online-${inv.id}`}
                          >
                            <CreditCard size={12} weight="bold" /> Pay Online
                          </button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}</TableBody></Table>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <div className="border border-border rounded-xl bg-card overflow-hidden">
            <Table><TableHeader><TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Receipt #</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Student</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider text-right">Amount</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Method</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Transaction</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-[10px] font-bold h-9 uppercase tracking-wider">Date</TableHead>
            </TableRow></TableHeader><TableBody>{payments.map(p => (
              <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                <TableCell className="text-[11px] py-2.5 font-mono">{p.receipt_number}</TableCell>
                <TableCell className="text-[12px] font-semibold py-2.5">{p.student_name}</TableCell>
                <TableCell className="text-[11px] py-2.5 text-right font-mono" style={{ color: '#10A882' }}>{fmt(p.amount)}</TableCell>
                <TableCell className="text-[10px] text-muted-foreground py-2.5 font-mono uppercase">{p.method}</TableCell>
                <TableCell className="text-[10px] text-muted-foreground py-2.5 font-mono">{p.transaction_id}</TableCell>
                <TableCell className="py-2.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: p.status === 'completed' ? '#10A882' : p.status === 'pending' ? '#E5A100' : '#808080' }}>{p.status}</span>
                </TableCell>
                <TableCell className="text-[10px] text-muted-foreground py-2.5 font-mono">{p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '-'}</TableCell>
              </TableRow>
            ))}</TableBody></Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Invoice Dialog */}
      <Dialog open={showEdit} onOpenChange={(open) => { if (!open) { setShowEdit(false); setEditInv(null); } }}>
        <DialogContent className="rounded-xl max-w-md" data-testid="edit-invoice-dialog">
          <DialogHeader><DialogTitle className="font-display text-foreground text-lg">Edit Invoice</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-3">
            <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Student</Label><Input data-testid="edit-student" value={editF.student_name} onChange={e => setEditF({...editF, student_name: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Type</Label>
                <select value={editF.type} onChange={e => setEditF({...editF, type: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground"><option value="registration">Registration</option><option value="admission">Admission</option><option value="tuition">Tuition</option><option value="material">Material</option><option value="exam">Exam</option></select></div>
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Amount</Label><Input data-testid="edit-amount" type="number" value={editF.amount} onChange={e => setEditF({...editF, amount: parseFloat(e.target.value) || 0})} className="h-9 rounded-lg text-sm" required /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Discount</Label><Input data-testid="edit-discount" type="number" value={editF.discount_amount} onChange={e => setEditF({...editF, discount_amount: parseFloat(e.target.value) || 0})} className="h-9 rounded-lg text-sm" /></div>
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Discount Reason</Label><Input data-testid="edit-discount-reason" value={editF.discount_reason} onChange={e => setEditF({...editF, discount_reason: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
            </div>
            <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Due Date</Label><Input type="date" value={editF.due_date} onChange={e => setEditF({...editF, due_date: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
            <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Notes</Label><Input data-testid="edit-notes" value={editF.notes} onChange={e => setEditF({...editF, notes: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
            <Button type="submit" data-testid="submit-edit-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>Save Changes</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* UPI QR Dialog */}
      <Dialog open={showUPI} onOpenChange={(open) => { if (!open) closeUPI(); }}>
        <DialogContent className="rounded-xl max-w-sm" data-testid="upi-qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-foreground text-lg flex items-center gap-2">
              <QrCode size={20} weight="bold" style={{ color: '#2B6EF0' }} /> Collect via UPI
            </DialogTitle>
          </DialogHeader>

          {upiStatus === 'generating' && (
            <div className="py-10 text-center">
              <Spinner size={28} className="animate-spin mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-3">Generating QR code...</p>
            </div>
          )}

          {upiStatus === 'error' && (
            <div className="py-10 text-center">
              <p className="text-sm text-destructive">Failed to generate QR</p>
              <Button onClick={closeUPI} variant="outline" className="mt-3 h-8 text-xs rounded-lg">Close</Button>
            </div>
          )}

          {(upiStatus === 'pending' || upiStatus === 'confirming') && upiData && (
            <div className="space-y-4">
              {/* Student & Amount */}
              <div className="text-center">
                <p className="text-[11px] text-muted-foreground">{upiData.invoice_number}</p>
                <p className="text-sm font-semibold text-foreground">{upiData.student_name}</p>
                <p className="font-display text-3xl font-extrabold mt-1" style={{ color: '#E04B2A' }}>{fmt(upiData.amount_due)}</p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center">
                <div className="p-3 bg-white rounded-xl border border-border" data-testid="upi-qr-code">
                  <QRCodeSVG value={upiData.upi_link} size={180} level="H" includeMargin={false} />
                </div>
              </div>

              {/* UPI ID */}
              <div className="flex items-center justify-center gap-2">
                <span className="text-[11px] text-muted-foreground">UPI ID:</span>
                <span className="font-mono text-[12px] text-foreground font-semibold">{upiData.upi_id}</span>
                <button onClick={copyUPI} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="copy-upi-id">
                  <Copy size={13} />
                </button>
              </div>

              <p className="text-[10px] text-muted-foreground text-center">
                Scan with any UPI app (GPay, PhonePe, Paytm)
              </p>

              {/* Polling indicator */}
              <div className="flex items-center justify-center gap-2 py-1">
                <ArrowClockwise size={12} className="animate-spin text-muted-foreground" />
                <span className="text-[10px] font-mono text-muted-foreground">Waiting for payment...</span>
              </div>

              {/* Manual confirm (for demo) */}
              <Button
                onClick={confirmManually}
                disabled={upiStatus === 'confirming'}
                className="w-full h-9 rounded-lg text-xs font-bold text-white"
                style={{ backgroundColor: '#10A882' }}
                data-testid="confirm-upi-btn"
              >
                {upiStatus === 'confirming' ? 'Confirming...' : 'Mark as Received (Manual)'}
              </Button>
            </div>
          )}

          {upiStatus === 'completed' && upiData && (
            <div className="py-6 text-center space-y-3" data-testid="upi-success">
              <CheckCircle size={48} weight="fill" className="mx-auto" style={{ color: '#10A882' }} />
              <p className="font-display text-lg font-bold text-foreground">Payment Received</p>
              <p className="text-sm text-muted-foreground">{fmt(upiData.amount_due)} from {upiData.student_name}</p>
              <p className="font-mono text-[10px] text-muted-foreground">TXN: {upiData.transaction_id}</p>
              <Button onClick={closeUPI} className="h-8 rounded-lg text-xs font-bold text-white mt-2" style={{ backgroundColor: '#E04B2A' }} data-testid="close-upi-btn">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <Card className="border border-border rounded-xl overflow-hidden">
      <CardContent className="p-4 relative">
        <div className="absolute top-0 left-0 w-full h-[3px]" style={{ backgroundColor: accent }} />
        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.1em] font-bold mt-1">{label}</p>
        <p className="font-display text-xl font-extrabold text-foreground mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}
